import {useLocalStorage} from '@/hooks/useLocalStorage';
import {buildIncidentFilters} from '@/utils/filterHelper';
import {FilterFactory} from '@dofleini/query-builder';
import {isEmpty} from 'lodash';
import PropTypes from 'prop-types';
import React, {createContext, useCallback, useContext, useEffect, useState} from 'react';
import {useLocation} from 'react-router-dom';

export const getSearch = (search, module) => (
  (search && typeof search === 'object') ? search?.[module] || '' : search || ''
);

const FilterContext = createContext();

function FilterProvider({id, children, defaultFilter, ...props}) {
  const [search, setSearch] = useState(null);
  const [filters, setFilters] = useState(defaultFilter);
  const [globalFilter, setGlobalFilter] = useState(defaultFilter);
  const [isOpen, setOpen] = useState(true);
  const {pathname} = useLocation();
  const [storage, setStorage] = useLocalStorage(`${id}_filters`);
  const [selectedValues, setSelectedValues] = useState();
  const [bodyParams, setBodyParams] = useState({});

  const closeFilter = useCallback(() => {
    setOpen(false);
  }, []);
  const openFilter = useCallback(() => {
    setOpen(true);
  }, []);
  const toggleFilter = useCallback(() => {
    setOpen(prevState => !prevState);
  }, []);

  const joinGlobalAndFilters = useCallback((filtersValue) => {
    setFilters(FilterFactory.add(filtersValue, globalFilter).toQuery());
  }, [globalFilter]);

  const buildGlobal = useCallback((values) => {
    buildIncidentFilters?.[id] && setGlobalFilter(buildIncidentFilters?.[id](values));
  }, [id]);

  const updateGlobal = useCallback((field, value) => {
    let result;
    setSelectedValues((prev) => {
      result = ({
        ...(prev || {}),
        [field]: value
      });
      return result;
    });

    if (result) {
      setStorage(result);
      buildGlobal(result);
    }
  }, [setStorage, buildGlobal]);

  const updateGlobalByState = useCallback((state) => {
    let result;
    setSelectedValues(prev => {
      result = {...prev, ...state};
      return result;
    });
    buildGlobal(result);
  }, [buildGlobal]);

  const resetFilters = useCallback(() => {
    setSelectedValues({});
    setStorage({});
    buildGlobal({});
  }, [setStorage, buildGlobal]);

  const handleSetSearch = useCallback((value, module) => {
    // Ensure we never spread a null/undefined previous state
    setSearch(prev => module ? ({...(prev || {}), [module]: value}) : value);
  }, []);

  useEffect(() => {
    if (setSelectedValues) {
      const isIncidence = pathname?.includes('incidents/list');
      if (isIncidence && !isEmpty(storage) && storage !== 'undefined') {
        updateGlobalByState(storage);
      } else {
        if (!isIncidence)
          setStorage({});
      }
    }
    // eslint-disable-next-line
  }, []);

  if (useFilter(id).id === id) {
    return children;
  }

  return (
    <FilterContext.Provider value={{
      id,
      isOpen,
      closeFilter,
      openFilter,
      toggleFilter,
      filter: filters,
      globalFilter,
      setFilter: joinGlobalAndFilters,
      setGlobalFilter,
      bodyParams,
      setBodyParams,
      search,
      setSearch: handleSetSearch,
      defaultFilter,
      updateGlobal,
      updateGlobalByState,
      resetFilters,
      selectedValues
    }} {...props}>
      {children}
    </FilterContext.Provider>
  );
}

function useFilter() {
  const context = useContext(FilterContext);
  if (context === undefined) {
    return {};
  }
  return context;
}

FilterProvider.propTypes = {
  children: PropTypes.any,
  defaultFilter: PropTypes.any,
  id: PropTypes.string
};

export {FilterProvider, useFilter};

